// Angular
import { OnInit, SimpleChange, OnChanges, OnDestroy } from '@angular/core';
import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Validators, FormGroup, FormControl } from '@angular/forms';

// Services
import { WISEService } from 'app/_services';

// Models
import { UniversalCalculationItem } from 'app/_models';
import { GroupResult, groupBy } from '@progress/kendo-data-query';
import { Subscription } from 'rxjs/Subscription';

// 3RD PARTY
import * as _ from 'underscore';

interface Item {
    text: string,
    value: string
}

interface BTWItem {
    text: string,
    value: string
}

@Component({
    selector: 'kendo-grid-edit-form',
    templateUrl: 'grid-edit-form-universalcalculation.component.html',
    styles: [
        'input[type=text] { width: 100%; }'
    ]
})
export class GridEditFormComponent implements OnInit, OnChanges, OnDestroy {
    public active = false;
    public editForm: FormGroup = new FormGroup({
        'PartID': new FormControl(),
        'PartCategory': new FormControl('', Validators.required),
        'PartDescription': new FormControl(0),
        'GeneralDescription': new FormControl(0),
        'YearOfPurchase': new FormControl(0),
        'DayValueAmount': new FormControl(0),
        'EstablishedDamageAmount': new FormControl(0),
        'VATType': new FormControl(),
        'WG': new FormControl(0),
        'SubReport': new FormControl(0)
    });

    public listWGItems: Array<Item> = [
        { text: 'Nieuwwaarde', value: 'NW' },
        { text: 'Dagwaarde', value: 'DW' },
        { text: 'Herstel', value: 'HS' },
        { text: 'Herbouwwaarde', value: 'HB' },
        { text: 'Inkoopwaarde', value: 'Ink' },
        { text: 'Verkoopwaarde', value: 'Verk' },
        { text: 'Waardevermindering', value: 'WM' },
        { text: 'Transactie', value: 'TR' }
    ];

    public lisTVATItems: Array<BTWItem> = [
        { text: 'Incl. BTW', value: 'I' },
        { text: 'Excl. BTW', value: 'E' }
    ];

    public partCategoryData: Array<any> = [];

    public formatOptionsYearOfPurchase: any = '';

    public groupedPartCategoryData: GroupResult[] = [];
    private subscriptions: Subscription[] = [];
    private selectedSubReport: any;
    private hasChildren = false;

    @Input() public isNew = false;
    @Input() public subReport = {};

    @Input() public set model(uci: UniversalCalculationItem) {
        this.editForm.reset(uci);

        this.active = uci !== undefined;
    }

    @Output() cancel: EventEmitter<any> = new EventEmitter();
    @Output() save: EventEmitter<UniversalCalculationItem> = new EventEmitter();

    constructor(
        private wiseService: WISEService,
    ) {
    }

    private initForm(): void {
        if (this.selectedSubReport) {
            this.wiseService.getUniBrandSubReportMenuItemList(this.selectedSubReport.CaseID, this.selectedSubReport.ProductionReportCalculationID)
                .subscribe((uniBrandDataResult: any) => {
                    if (uniBrandDataResult) {
                        const productionListitemListAsTypecode = uniBrandDataResult.ProductionListitemListAsTypecode;

                        this.partCategoryData = [];

                        const vatValue = _.find(this.lisTVATItems, { value: this.selectedSubReport.VatInclExclNvtTC });

                        this.setVATType(vatValue);

                        for (const productionListItem of productionListitemListAsTypecode) {
                            let item;

                            if (productionListItem.ChildTypeCodeItemList) {
                                this.hasChildren = true;

                                for (const productionChildListItem of productionListItem.ChildTypeCodeItemList) {
                                    item = {
                                        name: productionChildListItem.Text,
                                        category: productionChildListItem.Code,
                                        subcategory: productionListItem.Text
                                    };

                                    this.partCategoryData.push(item);
                                }
                            } else {
                                item = {
                                    name: productionListItem.Text,
                                    category: productionListItem.Code
                                };
                            }

                            this.partCategoryData.push(item);
                        }

                        if (this.hasChildren) {
                            this.groupedPartCategoryData = groupBy(this.partCategoryData, [{ field: 'subcategory' }]);
                        } else {
                            this.groupedPartCategoryData = this.partCategoryData;
                        }
                    }
                });
        }
    }

    private setVATType(vatValue: string) {
        if (vatValue) {
            this.editForm.get('VATType').patchValue(vatValue);
        }
    }

    public handleFilterPartCategory(value) {
        const filteredData = this.partCategoryData.filter((s) => s.name.toLowerCase().indexOf(value.toLowerCase()) !== -1);

        if (this.hasChildren) {
            this.groupedPartCategoryData = groupBy(filteredData, [{ field: 'subcategory' }]);
        } else {
            this.groupedPartCategoryData = filteredData;
        }
    }

    public selectionChangePartDescription(value: any): void {
        if (value && value.name) {
            const newValue = { 'PartDescription': value['name'] };
            this.editForm.patchValue(newValue, { onlySelf: true, emitEvent: true });
        }
    }

    public onSave(e): void {
        e.preventDefault();
        this.editForm.get('SubReport').patchValue(this.selectedSubReport);
        this.save.emit(this.editForm.value);
        this.active = false;
    }

    public onCancel(e): void {
        e.preventDefault();
        this.closeForm();
    }

    private closeForm(): void {
        this.active = false;
        this.cancel.emit();
    }

    public ngOnInit(): void {
        this.groupedPartCategoryData = groupBy(this.partCategoryData, [{ field: 'subcategory' }]);

        // this.editForm.get('PartDescription').valueChanges.subscribe(
        //     value => {
        //         console.log(value);
        //     }
        // );
    }

    ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
        if (changes.subReport) {
            if (changes.subReport.currentValue) {
                this.selectedSubReport = changes.subReport.currentValue;

                this.initForm();
            }
        }

        if (changes.model) {
            if (changes.model.currentValue && changes.model.currentValue.SubReport) {
                this.editForm.setValue(changes.model.currentValue);

                this.selectedSubReport = changes.model.currentValue.SubReport;

                this.initForm();
            }
        }
    }

    ngOnDestroy() {
        console.log('GridEditFormComponent: ngOnDestroy');

        if (this.subscriptions) {
            this.subscriptions.forEach(s => s.unsubscribe());
        }
    }
}
